import { booleanPointOnLine } from '@turf/boolean-point-on-line';
import { lineString, type Position } from '@turf/helpers';
import turfNearestPointOnLine from '@turf/nearest-point-on-line';
import isEqual from 'lodash.isequal';
import { GeoJSONFeature, MapMouseEvent } from 'mapbox-gl';
import { useCallback, useEffect, useRef, useState } from 'react';
import logError from '@alltrails/core/utils/logError';
import { LineProperties } from '@alltrails/types/CustomRoute';
import { useAppDispatch, useAppSelector } from '../../../redux';
import { addActiveSegmentHandle, selectCustomRouteActiveSegment } from '../../../redux/customRoute';
import { CustomRoutePath, CustomRouteSegment } from '../../../types/CustomRoute';
import { customRouteActiveSegmentPolylineId } from '../../../utils/layers';
import { reverseCoord } from '../../../utils/legacyGeoJSONConversions';

const nearestPointOnPath = (path: CustomRoutePath, lng: number, lat: number) => {
  // Apply reverseCoord and create line - use cleanCoords only if normal operation fails
  const reversedCoords = path.points.map(reverseCoord);

  // Turf's lineString throws when given fewer than two positions
  if (reversedCoords.length < 2) {
    return undefined;
  }

  const line = lineString(reversedCoords);

  let nearestPointResult;
  let replacementPoints;
  try {
    // Try normal operation first
    nearestPointResult = turfNearestPointOnLine(line, [lng, lat]);
  } catch {
    // Turf's nearestPointOnLine currently fails when there are adjacent duplicate points
    // https://github.com/Turfjs/turf/issues/2809
    // If nearestPointOnLine fails, manually remove adjacent duplicate coordinates and try again
    // Note that we do this manually rather than using turf's cleanCoords function as suggested in the issue above because cleanCoords removes more than just adjacent duplicate points and may significantly alter the geometry
    // https://turfjs.org/docs/api/cleanCoords
    // If we still see a high failure rate we can consider using cleanCoords
    const deduplicatedCoords = line.geometry.coordinates.reduce((acc, coord, index) => {
      if (index === 0 || coord[0] !== acc[acc.length - 1][0] || coord[1] !== acc[acc.length - 1][1]) {
        acc.push(coord);
      }

      return acc;
    }, [] as Position[]);

    if (deduplicatedCoords.length < 2) {
      return undefined;
    }

    nearestPointResult = turfNearestPointOnLine(lineString(deduplicatedCoords), [lng, lat]);

    // Since the deduplicated coordinates used to calculate the nearest point are a subset of the original coordinates, the index of the nearest point result won't be usable with the original coordinates
    // We will replace all of the points on the path with the deduplicated coordinates before adding the new handle
    replacementPoints = deduplicatedCoords.map(reverseCoord);
  }

  // Turf reports a negative index when it cannot find a nearest point
  if (!nearestPointResult || nearestPointResult.properties.index < 0) {
    return undefined;
  }

  const point = nearestPointResult.geometry.coordinates;
  const pointIndex = nearestPointResult.properties.index;
  return { point, pointIndex, replacementPoints };
};

const getPreferredClickedFeature = (features: GeoJSONFeature[] | undefined, preferedLinePosition: 'out' | 'back' | undefined) => {
  if (!preferedLinePosition) {
    // Since the click may have been on a layer above the polyline (e.g. OSM line or chevrons), find the first feature that is on the active segment polyline
    // The click may also return the same feature twice if that feature overlaps itself
    return features?.find(feature => feature.layer?.id === customRouteActiveSegmentPolylineId);
    // if there are multiple features on the active segment polyine, use the lowest index feature to place the point on the out and highest to place the point on the back
  } else {
    const activeSegmentFeatures = features?.filter(feature => feature.layer?.id === customRouteActiveSegmentPolylineId);
    const lowestIndexFeature = activeSegmentFeatures?.sort((a, b) => a.properties?.pathIndex - b.properties?.pathIndex)[0];
    const highestIndexFeature = activeSegmentFeatures?.sort((a, b) => b.properties?.pathIndex - a.properties?.pathIndex)[0];
    return preferedLinePosition === 'out' ? lowestIndexFeature : highestIndexFeature;
  }
};

// The clicked path of the active segment and the nearest point on it, shared by adding a handle and previewing where it would go
const getClickedPathPoint = (
  features: GeoJSONFeature[] | undefined,
  lng: number,
  lat: number,
  activeSegment: CustomRouteSegment | undefined,
  preferredLinePosition: 'out' | 'back' | undefined
) => {
  const feature = getPreferredClickedFeature(features, preferredLinePosition);
  if (!feature || !activeSegment) {
    return undefined;
  }

  const { pathIndex } = feature.properties as LineProperties;
  const path = activeSegment.paths[pathIndex];
  if (!path?.points) {
    return undefined;
  }

  const nearestPointResult = nearestPointOnPath(path, lng, lat);
  return nearestPointResult && { path, pathIndex, ...nearestPointResult };
};

/**
 * Where a click on the active segment polyline would place a new handle, as [longitude, latitude]
 * Previews the default placement, ignoring the out/back preference for overlapping lines since both share the same position
 */
export const getAddHandlePreviewPoint = (
  features: GeoJSONFeature[] | undefined,
  { lng, lat }: { lng: number; lat: number },
  activeSegment: CustomRouteSegment | undefined
) => {
  try {
    return getClickedPathPoint(features, lng, lat, activeSegment, undefined)?.point;
  } catch {
    // Not logged since this runs on every mousemove; addHandle logs the same failure if the user clicks
    return undefined;
  }
};

const getSecondOccurrenceOfPointOnPath = (
  path: CustomRoutePath,
  pathIndex: number,
  replacementPoints: Position[],
  pointIndex: number,
  lng: number,
  lat: number,
  point: Position
) => {
  const restOfPath = {
    points: replacementPoints ? replacementPoints.slice(pointIndex + 1) : path.points.slice(pointIndex + 1),
    freeDraw: path.freeDraw
  };

  // A second occurrence needs at least two remaining points to form a line
  if (restOfPath.points.length < 2) {
    return undefined;
  }

  const nearestPointResult = nearestPointOnPath(restOfPath as CustomRoutePath, lng, lat);
  if (!nearestPointResult) {
    return undefined;
  }

  const { point: restOfPathPoint, pointIndex: restOfPathPointIndex } = nearestPointResult;
  // a second occurrence of the point was found on the rest of the path
  if (isEqual(restOfPathPoint, point)) {
    return {
      // adjust for index since we only searched after the first occurrence of the point
      pointIndex: restOfPathPointIndex + (pointIndex + 1),
      pathIndex
    };
  }
  return undefined;
};

const useAddHandle = (preferredLinePosition: 'out' | 'back' | 'both' | undefined) => {
  const isPlacingSecondHandle = useRef(false);
  const dispatch = useAppDispatch();
  const activeSegment = useAppSelector(selectCustomRouteActiveSegment);
  const [multiHandleData, setMultiHandleData] = useState<{ afterPathIndex: number; point: [number, number] } | undefined>(undefined);

  // Adds a second handle when the user intends to add a multipoint handle (handle on both the "out" and "back" portions of an overlapping segment)
  // This must be done in an effect because the update to customRoute state from the first handle must be finished before adding the second.
  useEffect(() => {
    if (!!multiHandleData) {
      let pathWithSecondPoint: CustomRoutePath | undefined;
      let pathIndex: number | undefined;

      for (const [index, path] of activeSegment?.paths.entries() || []) {
        if (index > multiHandleData.afterPathIndex) {
          const reversedCoords = path.points.map(reverseCoord);
          // remove first point of the next path since it will be the same as the last point of the current path
          const coords = multiHandleData.afterPathIndex + 1 === index ? reversedCoords.slice(1) : reversedCoords;

          if (coords.length < 2) {
            continue;
          }

          if (booleanPointOnLine([multiHandleData.point[0], multiHandleData.point[1]], lineString(coords), { epsilon: 5e-10 })) {
            pathWithSecondPoint = path;
            pathIndex = index;
            break;
          }
        }
      }

      if (pathWithSecondPoint && pathIndex !== undefined) {
        // the nearest point will always be the first point of the next path, which is not what we are looking for
        // remove the first point of the next path and adjust for that removal if the point is on the next path
        const isPointFoundOnNextPath = pathIndex === multiHandleData.afterPathIndex + 1;
        const adjustedPath = isPointFoundOnNextPath
          ? { points: pathWithSecondPoint.points.slice(1), freeDraw: pathWithSecondPoint.freeDraw }
          : pathWithSecondPoint;
        const nearestPointResult = nearestPointOnPath(adjustedPath, multiHandleData.point[0], multiHandleData.point[1]);

        if (nearestPointResult) {
          const { point, pointIndex } = nearestPointResult;
          dispatch(
            addActiveSegmentHandle({
              pathIndex,
              pointIndex: isPointFoundOnNextPath ? pointIndex + 1 : pointIndex,
              latitude: point[1],
              longitude: point[0]
            })
          );
        }
      }
      isPlacingSecondHandle.current = false;
      setMultiHandleData(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [multiHandleData]);

  const addHandle = useCallback(
    (event: MapMouseEvent) => {
      try {
        const {
          lngLat: { lng, lat },
          features
        } = event;

        if (isPlacingSecondHandle.current) {
          return false;
        }

        // First occurrence of the nearest point on the path that was clicked
        const clickedPathPoint = getClickedPathPoint(
          features,
          lng,
          lat,
          activeSegment,
          preferredLinePosition === 'both' ? 'out' : preferredLinePosition
        );
        if (!clickedPathPoint) {
          return false;
        }

        const { path, pathIndex, point, pointIndex, replacementPoints } = clickedPathPoint;

        // Is that same point found again on the same path? (only necessary if a preference is set for specific marker placement when lines overlap)
        const secondOccurrencePoint = !!preferredLinePosition
          ? getSecondOccurrenceOfPointOnPath(path, pathIndex, replacementPoints, pointIndex, lng, lat, point)
          : undefined;

        dispatch(
          addActiveSegmentHandle({
            pathIndex: secondOccurrencePoint?.pathIndex && preferredLinePosition === 'back' ? secondOccurrencePoint.pathIndex : pathIndex,
            pointIndex: secondOccurrencePoint?.pointIndex && preferredLinePosition === 'back' ? secondOccurrencePoint.pointIndex : pointIndex,
            latitude: point[1],
            longitude: point[0],
            replacementPoints
          })
        );
        if (preferredLinePosition === 'both') {
          isPlacingSecondHandle.current = true;
          setMultiHandleData({ afterPathIndex: pathIndex, point });
        }
        return true;
      } catch (error) {
        // Keep the try/catch for edge cases in turf's geometry calculations
        logError(new Error('Failed to add a handle to the active custom route segment', { cause: error }));
        return false;
      }
    },
    [activeSegment, dispatch, preferredLinePosition]
  );

  return addHandle;
};

export default useAddHandle;
